import { useMutation, useQueryClient } from '@tanstack/react-query';
import { handleErrors } from '../../utils/errors';
import SharedDialog from '../../shared/shared-dialog';
import { Controller, useForm } from 'react-hook-form';
import { useStore } from '../state/zustand-store';
import { useAnalytics } from '../../shared/analytics/create-events';
import * as analytics from '../../shared/analytics/analytics.json';
import { ModuleList } from '../../shared/analytics/module-list';
import {
  AbstractCurrentSubscriptionModel,
  LicenseNotificationModel,
} from '../../api/billing-types';
import {
  BillingKeys,
  updateLicenseNotificationModel,
} from '../../api/billing-api';
import { useState } from 'react';
import AvatarChip from '../../shared/avatar-chip';
import SharedAutocomplete from '../../shared/shared-autocomplete';
import { emailRegex } from '../../utils/regular-expressions';
import { HubSpotCompany } from '../../api/billing-types';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
interface UpdateNotificationsFormProps {
  open: boolean;
  handleClose: () => void;
  subscription: AbstractCurrentSubscriptionModel;
  licenseNotificationModel: LicenseNotificationModel;
  hubSpotCompany: HubSpotCompany;
}

export default function UpdateNotificationsForm({
  open,
  handleClose,
  subscription,
  licenseNotificationModel,
  hubSpotCompany,
}: UpdateNotificationsFormProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [autoCompleteResponse, setAutoCompleteResponse] = useState('');
  const [emailError, setEmailError] = useState('');

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isDirty },
    watch,
    setValue,
  } = useForm<LicenseNotificationModel>({
    defaultValues: {
      ...licenseNotificationModel,
      companyName: licenseNotificationModel.companyName
        ? licenseNotificationModel.companyName
        : hubSpotCompany.properties.name,
      product: subscription.productModel.product,
    },
  });

  const onSubmit = (data: LicenseNotificationModel) => {
    mutate(data);
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (data: LicenseNotificationModel) =>
      updateLicenseNotificationModel(data),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Notification settings updated successfully!',
      });

      queryClient.invalidateQueries({
        queryKey: [
          BillingKeys.LicenseNotificationModel,
          subscription.productModel,
        ],
      });

      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue updating the notification settings. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        analytics.billing.subscriptions.update_notification_settings,
        {
          module: ModuleList.modules.billing.name,
          section: ModuleList.modules.billing.sections.subscriptions,
        }
      );
    },
  });

  const notificationContacts = watch('notificationContacts') || [];

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        reset();
        handleClose();
      }}
      title="Update Notifications"
      dialogContent={
        <Stack gap={1}>
          <Stack flexDirection="row">
            <Controller
              name="isActive"
              control={control}
              render={({ field: props }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...props}
                      checked={props.value}
                      onChange={(e) => props.onChange(e.target.checked)}
                    />
                  }
                  label="Active"
                />
              )}
            />
            <Controller
              name="ignoreThreshold"
              control={control}
              render={({ field: props }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...props}
                      checked={props.value}
                      onChange={(e) => props.onChange(e.target.checked)}
                    />
                  }
                  label="Ignore Threshold"
                />
              )}
            />
          </Stack>
          <Stack flexDirection="row" gap={3}>
            <Controller
              name={'licenseThreshold'}
              control={control}
              rules={{
                validate: (value) => {
                  if (value && value < 0) {
                    return false;
                  } else {
                    return true;
                  }
                },
              }}
              render={({ field }) => (
                <Tooltip
                  arrow
                  title="If your available license count goes below this threshold, you will get notified."
                >
                  <TextField
                    inputProps={{
                      id: 'licenseThreshold',
                      min: 0,
                    }}
                    value={field.value}
                    InputLabelProps={{
                      htmlFor: 'licenseThreshold',
                    }}
                    label="License Threshold"
                    fullWidth
                    autoComplete="off"
                    type={'number'}
                    onChange={field.onChange}
                    error={!!errors.licenseThreshold}
                    sx={{ width: 180 }}
                  />
                </Tooltip>
              )}
            />
            <Controller
              name={'sendOnlyEveryNumberOfDays'}
              control={control}
              rules={{
                validate: (value) => {
                  if (value && value < 0) {
                    return false;
                  } else {
                    return true;
                  }
                },
              }}
              render={({ field }) => (
                <Tooltip
                  arrow
                  title="License count notifications are sent out everyday until you add more licenses. If you do not want to receive a notification everyday, you can manage the number of days between notifications."
                >
                  <TextField
                    inputProps={{
                      id: 'sendOnlyEveryNumberOfDays',
                      min: 0,
                    }}
                    value={field.value}
                    InputLabelProps={{
                      htmlFor: 'sendOnlyEveryNumberOfDays',
                    }}
                    label="Number of Days"
                    fullWidth
                    type={'number'}
                    onChange={field.onChange}
                    autoComplete="off"
                    error={!!errors.sendOnlyEveryNumberOfDays}
                    sx={{ width: 180 }}
                  />
                </Tooltip>
              )}
            />
          </Stack>
          <Stack sx={{ pt: 1 }}>
            <Typography sx={{ pb: 1 }}>Emails to Notify:</Typography>
            <Stack flexDirection="row" gap={1}>
              <Controller
                name={'notificationContacts'}
                control={control}
                render={() => (
                  <Box sx={{ width: 450 }}>
                    <SharedAutocomplete
                      freeSolo
                      sources={['users']}
                      autocompleteValue={autoCompleteResponse}
                      setAutoCompleteValue={setAutoCompleteResponse}
                      errors={emailError.length > 0}
                      helperText={emailError.length > 0 ? emailError : ' '}
                      onSelection={(value) => {
                        if (!value) return;
                        // Email validation
                        if (!emailRegex.test(value)) {
                          setEmailError('Please enter a valid email address.');
                          return;
                        }
                        setEmailError('');
                        if (notificationContacts) {
                          if (notificationContacts.includes(value)) {
                            setEmailError('This email is already added.');
                            return;
                          }
                          setValue(
                            'notificationContacts',
                            [...notificationContacts, value],
                            { shouldDirty: true }
                          );
                        } else {
                          setValue('notificationContacts', [value], {
                            shouldDirty: true,
                          });
                        }

                        setAutoCompleteResponse('');
                      }}
                    />
                  </Box>
                )}
              />
            </Stack>
            <Stack flexDirection="row" gap={1} flexWrap="wrap">
              {notificationContacts.length > 0 ? (
                notificationContacts.map((user) => {
                  return (
                    <AvatarChip
                      key={user}
                      label={user}
                      type={'User'}
                      email={user}
                      onDelete={() => {
                        setValue(
                          'notificationContacts',
                          notificationContacts.filter(
                            (notifiedUser) => notifiedUser !== user
                          )
                        );
                      }}
                    />
                  );
                })
              ) : (
                <Box height={24} />
              )}
            </Stack>
          </Stack>
        </Stack>
      }
      primaryButton={{
        loading: isPending,
        action: handleSubmit(onSubmit),
        disabled: isPending || !isDirty || notificationContacts.length === 0,
        title: 'Update Notification',
      }}
    />
  );
}
